import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Search, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success('Willkommen zurück!');
      navigate(data.company?.onboarding_completed ? '/dashboard' : '/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-emerald-700 relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/30 via-transparent to-emerald-900/30" />
        <div className="relative z-10 max-w-md">
          <div className="bg-white rounded-xl p-3 w-fit mb-8">
            <img src="/logo.png" alt="LaizyLeads" className="h-10 w-auto" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4 tracking-tight">Willkommen bei LaizyLeads</h1>
          <p className="text-lg text-emerald-100 leading-relaxed">Identifizieren Sie Ihre idealen B2B-Kunden automatisch. Analysieren, entdecken und kontaktieren Sie die richtigen Interessenten.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md border-0 shadow-none">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-2 mb-4">
              <img src="/logo.png" alt="LaizyLeads" className="h-8 w-auto" />
              <span className="font-semibold text-lg">LaizyLeads</span>
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight">Anmelden</CardTitle>
            <CardDescription>Geben Sie Ihre Zugangsdaten ein, um auf Ihr Konto zuzugreifen</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input id="email" type="email" placeholder="name@firma.de" value={email} onChange={e => setEmail(e.target.value)} required data-testid="login-email-input" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Passwort</Label>
                  <Link to="/password-reset" className="text-xs text-emerald-600 hover:underline" data-testid="forgot-password-link">Passwort vergessen?</Link>
                </div>
                <Input id="password" type="password" placeholder="Passwort eingeben" value={password} onChange={e => setPassword(e.target.value)} required data-testid="login-password-input" />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" disabled={loading} data-testid="login-submit-btn">
                {loading ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Anmeldung...</span> : <span className="flex items-center gap-2">Anmelden <ArrowRight className="w-4 h-4" /></span>}
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-muted-foreground">
              Noch kein Konto?{' '}
              <Link to="/register" className="text-emerald-600 font-medium hover:underline" data-testid="register-link">Unternehmen registrieren</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
